import { guestApiClient } from './apiClient';
import { MenuItem, MenuCategory, Order, CartItem, ApiResponse } from '../types';

export interface PlaceOrderRequest {
  items: {
    menu_item_id: string;
    quantity: number;
    special_instructions?: string;
  }[];
  delivery_location: 'room' | 'restaurant' | 'lobby';
  delivery_instructions?: string;
}

class OrderingService {
  /**
   * Get all menu categories with items
   */
  async getMenuCategories(): Promise<ApiResponse<MenuCategory[]>> {
    try {
      const response = await guestApiClient.get<MenuCategory[]>('/api/guest/menu/categories/');
      
      // Ensure response.data is always an array (skip validation for now as backend might return nested format)
      if (!response.success) {
        console.warn('getMenuCategories: API returned unsuccessful response:', response);
        return {
          success: true,
          data: [] // Return empty array as fallback
        };
      }
      
      // Handle potential nested response format from guest API
      const menuData = response.data;
      if (!menuData) {
        console.warn('getMenuCategories: API returned null data');
        return {
          success: true,
          data: []
        };
      }
      
      // If data is already an array, return as-is
      if (Array.isArray(menuData)) {
        return response;
      }
      
      // If data is nested (e.g., {data: [...]}), extract the array
      if (typeof menuData === 'object' && menuData !== null && 'data' in menuData && Array.isArray((menuData as any).data)) {
        return {
          success: true,
          data: (menuData as any).data
        };
      }
      
      console.warn('getMenuCategories: Unexpected data format:', menuData);
      return {
        success: true,
        data: []
      };
    } catch (error: any) {
      console.error('getMenuCategories: API error:', error);
      throw error;
    }
  }

  /**
   * Get menu items by category
   */
  async getMenuItemsByCategory(categoryId: string): Promise<ApiResponse<MenuItem[]>> {
    try {
      const response = await guestApiClient.get<MenuItem[]>(`/api/guest/menu/categories/${categoryId}/items/`);
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Get a specific menu item
   */
  async getMenuItem(itemId: string): Promise<ApiResponse<MenuItem>> {
    try {
      const response = await guestApiClient.get<MenuItem>(`/api/guest/menu/items/${itemId}/`);
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Place a new order
   */
  async placeOrder(orderData: PlaceOrderRequest): Promise<ApiResponse<Order>> {
    try {
      const response = await guestApiClient.post<Order>('/api/guest/orders/', orderData);
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Get current user's orders
   */
  async getMyOrders(): Promise<ApiResponse<Order[]>> {
    try {
      const response = await guestApiClient.get<Order[]>('/api/guest/orders/');
      
      // Ensure response.data is always an array
      if (!response.success) {
        console.warn('getMyOrders: API returned unsuccessful response:', response);
        return {
          success: true,
          data: [] // Return empty array as fallback
        };
      }
      
      // Handle potential nested response format from guest API
      const ordersData = response.data;
      if (!ordersData) {
        console.warn('getMyOrders: API returned null data');
        return {
          success: true,
          data: []
        };
      }
      
      // If data is already an array, return as-is
      if (Array.isArray(ordersData)) {
        return response;
      }
      
      // If data is nested (e.g., {data: [...]}), extract the array
      if (typeof ordersData === 'object' && ordersData !== null && 'data' in ordersData && Array.isArray((ordersData as any).data)) {
        return {
          success: true,
          data: (ordersData as any).data
        };
      }
      
      console.warn('getMyOrders: Unexpected data format, returning empty array:', ordersData);
      return {
        success: true,
        data: []
      };
    } catch (error: any) {
      console.error('getMyOrders: API error:', error);
      throw error;
    }
  }

  /**
   * Get specific order details
   */
  async getOrder(orderId: string): Promise<ApiResponse<Order>> {
    try {
      const response = await guestApiClient.get<Order>(`/api/guest/orders/${orderId}/`);
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Cancel an order
   */
  async cancelOrder(orderId: string): Promise<ApiResponse<{ message: string }>> {
    try {
      const response = await guestApiClient.patch<{ message: string }>(`/api/guest/orders/${orderId}/cancel/`);
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Get order status updates
   */
  async getOrderStatus(orderId: string): Promise<ApiResponse<{ status: string; estimated_delivery_time?: string }>> {
    try {
      const response = await guestApiClient.get<{ status: string; estimated_delivery_time?: string }>(`/api/guest/orders/${orderId}/status/`);
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Calculate the cart subtotal. Taxes are the server's job — it applies
   * the hotel's real rates when the order posts, so inventing a rate here
   * would show the guest a number the folio won't match.
   */
  calculateOrderTotal(items: CartItem[]): { subtotal: number; tax: number; total: number } {
    const subtotal = items.reduce((sum, item) => sum + item.total_price, 0);

    return {
      subtotal: Math.round(subtotal * 100) / 100,
      tax: 0,
      total: Math.round(subtotal * 100) / 100,
    };
  }
}

export const orderingService = new OrderingService();